import { Box, Typography, styled } from "@mui/material";
import { colors } from "../../theme/colors";

export const SelectionModalTabs = styled(Box)({
  display: "flex",
  width: "100%",
  gap: 0,
  padding: "0 10px",
  boxSizing: "border-box",
  marginBottom: "16px",
});

export const SelectionModalTab = styled("button", {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "disabled",
})<{ active?: boolean; disabled?: boolean }>(({ active, disabled }) => ({
  flex: 1,
  border: "none",
  background: "transparent",
  padding: "0 0 10px",
  margin: 0,
  fontFamily: "Nunito, sans-serif",
  fontSize: "14px",
  fontWeight: active ? 700 : 600,
  color: disabled
    ? colors.text.secondary
    : active
      ? colors.primary.main
      : colors.text.primary,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  textAlign: "center",
  borderBottom: active
    ? `2px solid ${colors.primary.main}`
    : "2px solid transparent",
}));

export const SelectionModalSection = styled(Box)({
  marginBottom: "16px",
  "&:last-child": {
    marginBottom: 0,
  },
});

export const SelectionSectionHeader = styled(Box)({
  width: "100%",
  height: "35px",
  display: "flex",
  alignItems: "center",
  paddingLeft: "20px",
  backgroundColor: colors.background.default,
  marginBottom: "10px",
  gap: "4px",
});

export const SelectionSectionHeaderTitle = styled(Typography)({
  fontFamily: "Nunito, sans-serif",
  fontSize: "14px",
  fontWeight: 700,
  color: colors.text.secondary,
  lineHeight: 1,
});

export const SelectionSectionHeaderOption = styled("button", {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>(({ active }) => ({
  border: "none",
  background: "transparent",
  padding: 0,
  margin: 0,
  fontFamily: "Nunito, sans-serif",
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: 1,
  color: active ? colors.text.secondary : colors.border.medium,
  cursor: "pointer",
}));

export const SelectionSectionHeaderSeparator = styled("span")({
  fontFamily: "Nunito, sans-serif",
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: 1,
  color: colors.border.medium,
});

export const SelectionModalSectionBody = styled(Box)({
  width: "100%",
  padding: "0 20px",
  boxSizing: "border-box",
});

export const SelectionModalSectionBodySpaced = styled(SelectionModalSectionBody)({
  marginBottom: "16px",
});

/** @deprecated Use SelectionSectionHeader + SelectionSectionHeaderTitle */
export const SelectionModalSectionTitle = SelectionSectionHeaderTitle;

export const DentitionToggle = styled(SelectionSectionHeader)({
  padding: "0 10px",
});
export const DentitionToggleButton = SelectionSectionHeaderOption;

export const ToothQuadrantRow = styled(Box)({
  display: "flex",
  alignItems: "stretch",
  width: "100%",
  gap: "18px",
  marginBottom: "18px",
  "&:last-child": {
    marginBottom: 0,
  },
});

export const ToothQuadrant = styled(Box, {
  shouldForwardProp: (prop) => prop !== "quadrantPosition",
})<{
  quadrantPosition?: "upperRight" | "upperLeft" | "lowerRight" | "lowerLeft";
}>(({ quadrantPosition }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
  gap: "10px",
  justifyContent:
    quadrantPosition === "lowerRight" || quadrantPosition === "lowerLeft"
      ? "flex-end"
      : "flex-start",
}));

export const ToothQuadrantLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "align",
})<{ align?: "left" | "right" }>(({ align = "left" }) => ({
  fontFamily: "Nunito, sans-serif",
  fontSize: "14px",
  fontWeight: 700,
  color: colors.text.primary,
  width: "100%",
  textAlign: align,
  lineHeight: 1,
}));

export const ToothButtonsRow = styled(Box)({
  display: "flex",
  width: "100%",
  gap: "4px",
});

export const SelectionOptionButton = styled("button", {
  shouldForwardProp: (prop) =>
    prop !== "selected" && prop !== "size" && prop !== "disabled",
})<{ selected?: boolean; size?: "sm" | "md" | "lg"; disabled?: boolean }>(
  ({ selected, size = "sm", disabled }) => ({
    border: `1px solid ${disabled ? colors.border.medium : colors.primary.main}`,
    background: selected
      ? colors.primary.main
      : colors.background.secondary,
    color: selected
      ? colors.text.white
      : disabled
        ? colors.text.secondary
        : colors.primary.main,
    fontFamily: "Nunito, sans-serif",
    fontSize: "14px",
    fontWeight: 700,
    flex: size === "lg" ? undefined : 1,
    minWidth: size === "lg" ? undefined : 0,
    width: size === "lg" ? "100%" : undefined,
    height: size === "lg" ? "48px" : size === "md" ? "31px" : "26px",
    borderRadius: "8px",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled && !selected ? 0.55 : 1,
    padding: size === "lg" ? "8px 12px" : "0 4px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }),
);

export const SelectionOptionTooltipTarget = styled("span")({
  flex: 1,
  minWidth: 0,
  display: "inline-flex",
});

export const CustomInputRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "100%",
});

export const CustomInput = styled("input")({
  flex: 1,
  minWidth: 0,
  height: "48px",
  border: `1px solid ${colors.border.medium}`,
  borderRadius: "8px",
  padding: "0 12px",
  fontFamily: "Nunito, sans-serif",
  fontSize: "16px",
  color: colors.text.primary,
  outline: "none",
  "&::placeholder": {
    color: colors.border.medium,
    opacity: 1,
  },
  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.6,
    background: colors.background.secondary,
  },
});

export const SelectionTextInput = styled(CustomInput)({
  width: "100%",
  flex: "none",
});

export const CustomAddButton = styled("button")({
  border: "none",
  background: colors.primary.main,
  color: colors.text.white,
  fontFamily: "Nunito, sans-serif",
  fontSize: "14px",
  fontWeight: 700,
  borderRadius: "8px",
  height: "40px",
  padding: "0 16px",
  cursor: "pointer",
  whiteSpace: "nowrap",
  flexShrink: 0,
  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.55,
  },
});

export const RangeFieldsRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "10px",
  width: "100%",
  marginBottom: "16px",
});

export const RangeField = styled(Box)({
  minWidth: 0,
});

export const RangeFieldLabel = styled(Typography)({
  fontFamily: "Nunito, sans-serif",
  fontSize: "12px",
  fontWeight: 400,
  marginLeft: "8px",
  color: colors.text.primary,
  marginBottom: "2px",
});

export const FullWidthActionButton = styled("button")<{
  disabled?: boolean;
}>(({ disabled }) => ({
  width: "100%",
  border: "none",
  background: disabled ? colors.border.medium : colors.primary.main,
  color: colors.text.white,
  fontFamily: "Nunito, sans-serif",
  fontSize: "14px",
  fontWeight: 700,
  borderRadius: "8px",
  height: "40px",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.7 : 1,
}));

export const RangeLimitError = styled(Typography)({
  fontFamily: "Nunito, sans-serif",
  fontSize: "12px",
  fontWeight: 600,
  color: colors.status.error,
  marginTop: "8px",
  textAlign: "center",
});

export const SurfaceOptionsRow = styled(Box)({
  display: "flex",
  width: "100%",
  gap: "6px",
});

export const QuadGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "8px",
  width: "100%",
});

export const QuadOptionButton = styled(SelectionOptionButton)({
  display: "inline-flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  height: "31px",
  minHeight: "31px",
  width: "100%",
  whiteSpace: "nowrap",
  padding: "0 8px",
});

export const QuadOptionCode = styled("span")({
  fontFamily: "Nunito, sans-serif",
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: 1,
  flexShrink: 0,
});

export const QuadOptionLabel = styled("span")({
  fontFamily: "Nunito, sans-serif",
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: 1,
  flexShrink: 0,
});

export const ArchOptionsRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "8px",
  width: "100%",
});

export const ArchOptionButton = styled(QuadOptionButton)({});
